/**
 * PubblicaDialog — l'unico passaggio che manda un contenuto in onda.
 *
 * Tre cose non sono negoziabili, e sono tutte qui:
 *  1. la NATURA della modifica la dichiara l'umano, con le parole del mestiere
 *     («aggiornamento del contenuto» / «correzione di forma»), non con `major`
 *     e `minor`. Cambiare «può» in «non può» sono quattro caratteri e ribalta il
 *     significato: nessuna soglia di byte può deciderlo;
 *  2. il MOTIVO è obbligatorio e resta nel dato: è ciò che si legge fra sei mesi
 *     alla voce «vedi cosa è cambiato»;
 *  3. se l'editore dichiara «correzione di forma» ma i segnali dicono il
 *     contrario, si mostra la spiegazione e si chiede una conferma esplicita. Non
 *     si corregge la sua scelta a sua insaputa, e non si lascia passare in
 *     silenzio una modifica sostanziale che non avviserebbe nessuno.
 *
 * La validazione di pubblicazione è la STESSA funzione che gira nella Cloud
 * Function (`validaContenuto(..., { perPubblicazione: true })`): qui serve a
 * dire cosa manca PRIMA di chiamare, là è il gate reale.
 *
 * Doc canonico: docs/01-ARCHITECTURE/KB-CONTENT-MODEL.md
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Checkbox, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControlLabel, Paper, Radio,
  RadioGroup, Stack, TextField, Typography,
} from '@mui/material';
import { PublishOutlined } from '@mui/icons-material';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import { pubblicaKbContenuto } from '../../services/kbContentsService';
import { formattaData } from './labels';
import {
  KB_TIPO_MODIFICA_LABEL,
  validaContenuto,
  type KbErroreValidazione,
} from '@shared/kb/kbContentModel';
import {
  classificaModifica,
  formattaVersione,
  richiedeConfermaSuMinore,
  versioneSuccessiva,
} from '@shared/kb/kbVersioning';
import { aMillis, type KbContenuto, type KbTipoModifica } from '@shared/kb/kbTypes';
import type { KbCorpoCaricato, PubblicaKbResponse } from '../../types/kb';

interface Props {
  open: boolean;
  /** Testata come è adesso sul database (o come l'editor l'ha appena salvata). */
  contenuto: KbContenuto | null;
  /** Corpo che andrà in onda: la bozza salvata. */
  corpoDaPubblicare: string;
  /** Corpo attualmente pubblicato: il «prima» del confronto. `null` alla prima volta. */
  corpoPubblicato: KbCorpoCaricato | null;
  onClose: () => void;
  onPubblicato: (esito: PubblicaKbResponse) => void;
}

const TOUCH_MIN = 40;

/**
 * Estrae l'elenco dei campi mancanti da un errore della callable.
 * La CF lancia `invalid-argument` con gli errori nei `details`: se ci sono si
 * mostrano campo per campo, altrimenti resta il messaggio. Nessun `catch` muto.
 */
function erroriDaDetails(errore: unknown): KbErroreValidazione[] {
  const details = (errore as { details?: unknown })?.details;
  const grezzi = Array.isArray(details)
    ? details
    : Array.isArray((details as { errori?: unknown })?.errori)
      ? ((details as { errori: unknown[] }).errori)
      : [];
  const out: KbErroreValidazione[] = [];
  for (const e of grezzi) {
    if (e && typeof e === 'object' && 'campo' in e && 'messaggio' in e) {
      out.push({
        campo: String((e as KbErroreValidazione).campo),
        messaggio: String((e as KbErroreValidazione).messaggio),
      });
    }
  }
  return out;
}

export const PubblicaDialog: React.FC<Props> = ({
  open, contenuto, corpoDaPubblicare, corpoPubblicato, onClose, onPubblicato,
}) => {
  const primaPubblicazione = corpoPubblicato === null;

  const [tipo, setTipo] = useState<KbTipoModifica | ''>('');
  const [motivo, setMotivo] = useState('');
  const [confermaMinore, setConfermaMinore] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [erroriCampo, setErroriCampo] = useState<KbErroreValidazione[]>([]);

  useEffect(() => {
    if (!open) return;
    // Alla prima pubblicazione non c'è nulla da confrontare: la scelta resta
    // visibile ma parte da «aggiornamento del contenuto», che è ciò che è.
    setTipo(primaPubblicazione ? 'maggiore' : '');
    setMotivo(primaPubblicazione ? 'prima pubblicazione' : '');
    setConfermaMinore(false);
    setErrore(null);
    setErroriCampo([]);
    setInCorso(false);
  }, [open, primaPubblicazione]);

  /** Segnali e proposta del sistema. Assenti alla prima pubblicazione. */
  const proposta = useMemo(() => {
    if (!contenuto || !corpoPubblicato) return null;
    return classificaModifica(
      {
        titolo: corpoPubblicato.titolo_snapshot ?? '',
        riassunto: corpoPubblicato.riassunto_snapshot ?? '',
        corpo_markdown: corpoPubblicato.corpo_markdown ?? '',
      },
      {
        titolo: contenuto.titolo ?? '',
        riassunto: contenuto.riassunto ?? '',
        corpo_markdown: corpoDaPubblicare,
      },
    );
  }, [contenuto, corpoPubblicato, corpoDaPubblicare]);

  const confermaRichiesta =
    tipo !== '' && proposta !== null && richiedeConfermaSuMinore(tipo, proposta);

  /** Esito della validazione di pubblicazione, con il motivo appena scritto dentro. */
  const esitoValidazione = useMemo(() => {
    if (!contenuto) return null;
    return validaContenuto(
      { ...contenuto, motivo_ultima_modifica: motivo },
      { corpoMarkdown: corpoDaPubblicare, perPubblicazione: true },
    );
  }, [contenuto, motivo, corpoDaPubblicare]);

  const versioneRisultante = useMemo(() => {
    if (!contenuto || tipo === '') return null;
    const successiva = versioneSuccessiva(
      {
        versione_maggiore: contenuto.versione_maggiore,
        versione_minore: contenuto.versione_minore,
      },
      tipo,
    );
    return formattaVersione(successiva.versione_maggiore, successiva.versione_minore);
  }, [contenuto, tipo]);

  const bloccato =
    inCorso ||
    tipo === '' ||
    !motivo.trim() ||
    (confermaRichiesta && !confermaMinore) ||
    esitoValidazione?.valido === false;

  const handlePubblica = async () => {
    if (!contenuto || tipo === '') return;
    setInCorso(true);
    setErrore(null);
    setErroriCampo([]);
    try {
      const esito = await pubblicaKbContenuto({
        content_id: contenuto.id,
        tipo_modifica: tipo,
        motivo: motivo.trim(),
      });
      onPubblicato(esito);
      onClose();
    } catch (e) {
      const dettagli = erroriDaDetails(e);
      setErroriCampo(dettagli);
      setErrore(
        dettagli.length > 0
          ? 'La pubblicazione è stata rifiutata: mancano dei dati obbligatori.'
          : e instanceof Error
            ? e.message
            : 'Errore durante la pubblicazione',
      );
    } finally {
      setInCorso(false);
    }
  };

  const erroriDaMostrare =
    erroriCampo.length > 0 ? erroriCampo : (esitoValidazione?.errori ?? []);

  return (
    <Dialog open={open} onClose={inCorso ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: tokens.font.display, fontWeight: 600 }}>
        Pubblica «{contenuto?.titolo ?? ''}»
      </DialogTitle>
      <DialogContent dividers>
        {errore && <Alert severity="error" sx={{ mb: 2 }}>{errore}</Alert>}

        {erroriDaMostrare.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>Prima di pubblicare manca questo</AlertTitle>
            <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, my: 0 }}>
              {erroriDaMostrare.map((e) => (
                <Typography component="li" variant="body2" key={`${e.campo}-${e.messaggio}`}>
                  <Box component="span" sx={{ fontFamily: tokens.font.mono, fontSize: '0.78rem' }}>
                    {e.campo}
                  </Box>
                  {' — '}
                  {e.messaggio}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        <Stack spacing={2.5}>
          {/* ── STATO ATTUALE ── */}
          {contenuto && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <SoftChip
                label={`versione attuale ${formattaVersione(contenuto.versione_maggiore, contenuto.versione_minore)}`}
                tone="default"
                mono
              />
              {contenuto.pubblicato_at != null && (
                <SoftChip
                  label={`pubblicato il ${formattaData(aMillis(contenuto.pubblicato_at))}`}
                  tone="default"
                />
              )}
              {versioneRisultante && (
                <SoftChip label={`diventerà ${versioneRisultante}`} tone="success" mono />
              )}
            </Stack>
          )}

          {primaPubblicazione && (
            <Alert severity="info">
              È la prima pubblicazione: non c&apos;è nessuna versione precedente con cui
              confrontare il testo.
            </Alert>
          )}

          {/* ── NATURA DELLA MODIFICA ── */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: tokens.color.graphite, mb: 0.5 }}>
              Che tipo di modifica è? *
            </Typography>
            <RadioGroup value={tipo} onChange={(e) => setTipo(e.target.value as KbTipoModifica)}>
              <FormControlLabel
                value="maggiore"
                disabled={inCorso}
                sx={{ minHeight: TOUCH_MIN, touchAction: 'manipulation', alignItems: 'flex-start', mr: 0 }}
                control={<Radio sx={{ p: 1.25 }} />}
                label={
                  <Box sx={{ py: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {KB_TIPO_MODIFICA_LABEL.maggiore}
                    </Typography>
                    <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                      cambia cosa dice o cosa si deve fare: la versione principale sale e la data
                      di verifica si azzera
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="minore"
                disabled={inCorso}
                sx={{ minHeight: TOUCH_MIN, touchAction: 'manipulation', alignItems: 'flex-start', mr: 0 }}
                control={<Radio sx={{ p: 1.25 }} />}
                label={
                  <Box sx={{ py: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {KB_TIPO_MODIFICA_LABEL.minore}
                    </Typography>
                    <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                      refusi, punteggiatura, formattazione: il significato non cambia
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
            {tipo === '' && (
              <Typography variant="caption" sx={{ color: tokens.color.warning }}>
                Scegli una delle due: nessuna delle due è il valore di partenza.
              </Typography>
            )}
          </Box>

          {/* ── CONFERMA SU «CORREZIONE DI FORMA» SMENTITA DAI SEGNALI ── */}
          {confermaRichiesta && proposta && (
            <Alert severity="warning">
              <AlertTitle sx={{ fontWeight: 700 }}>
                Al sistema non sembra una correzione di forma
              </AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {proposta.spiegazione}.
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75, mb: 1 }}>
                {proposta.segnali.titolo_cambiato && <SoftChip label="titolo cambiato" tone="warning" />}
                {proposta.segnali.riassunto_cambiato && <SoftChip label="riassunto cambiato" tone="warning" />}
                {proposta.segnali.struttura_cambiata && <SoftChip label="sezioni cambiate" tone="warning" />}
                <SoftChip
                  label={`testo diverso al ${Math.round(proposta.segnali.quota_testo_cambiato * 100)}%`}
                  tone="default"
                  mono
                />
              </Stack>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                Se è davvero solo forma, confermalo: una correzione di forma non azzera la data di
                verifica e non è annunciabile.
              </Typography>
              <FormControlLabel
                sx={{ minHeight: TOUCH_MIN, touchAction: 'manipulation' }}
                control={
                  <Checkbox
                    checked={confermaMinore}
                    disabled={inCorso}
                    onChange={(e) => setConfermaMinore(e.target.checked)}
                    sx={{ p: 1.25 }}
                  />
                }
                label={
                  <Typography variant="body2">
                    Confermo: è una correzione di forma
                  </Typography>
                }
              />
            </Alert>
          )}

          {/* ── PROPOSTA CONCORDE (informativa) ── */}
          {!confermaRichiesta && proposta && tipo !== '' && (
            <Paper
              variant="outlined"
              sx={{ p: 1.5, borderColor: tokens.color.line, bgcolor: tokens.color.bone }}
            >
              <Typography variant="caption" sx={{ color: tokens.color.ash }}>
                Il sistema la vedeva così: {proposta.spiegazione}.
              </Typography>
            </Paper>
          )}

          {/* ── MOTIVO ── */}
          <TextField
            label="Motivo della modifica"
            required
            value={motivo}
            disabled={inCorso}
            onChange={(e) => setMotivo(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            error={!motivo.trim()}
            helperText={
              motivo.trim()
                ? 'Resta nel dato: è ciò che si legge alla voce «vedi cosa è cambiato».'
                : 'Obbligatorio: scrivi cosa è cambiato e perché.'
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={inCorso} sx={{ minHeight: TOUCH_MIN }}>
          Annulla
        </Button>
        <Button
          variant="contained"
          startIcon={inCorso ? <CircularProgress size={14} color="inherit" /> : <PublishOutlined />}
          disabled={bloccato}
          onClick={handlePubblica}
          sx={{ minHeight: TOUCH_MIN, touchAction: 'manipulation' }}
        >
          Pubblica
        </Button>
      </DialogActions>
    </Dialog>
  );
};
